// The user's library: their own recipes, cookbooks, mealplan templates, units,
// and their own/edited ingredients & utensils. Built-in catalog items and country cookbooks
// live in content/ and are merged in by the selectors at the bottom.
import { useMemo } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { nanoid } from 'nanoid/non-secure';
import { persistStorage } from './storage';
import { CATALOG_INGREDIENTS, CATALOG_UTENSILS, CONTENT_RECIPES } from '../../content';
import type { CustomUnit, Cookbook, Diet, Id, Ingredient, Mealplan, Recipe, Utensil } from '../types';

export type LibraryState = {
  recipes: Record<Id, Recipe>;
  cookbooks: Record<Id, Cookbook>;
  mealplans: Record<Id, Mealplan>;
  units: Record<Id, CustomUnit>;
  /** User-created ingredients and edited copies of built-in ones (same id overrides). */
  ingredients: Record<Id, Ingredient>;
  utensils: Record<Id, Utensil>;
  hiddenBuiltIns: Id[];
  /** Diets a person confirmed a recipe fits, by its id (own and country-cookbook ones). */
  dietConfirmations: Record<Id, Diet[]>;
  updatedAt: number;
};

type Kind = 'recipes' | 'cookbooks' | 'mealplans' | 'units' | 'ingredients' | 'utensils';
type ItemOf<K extends Kind> = LibraryState[K][string];

type Actions = {
  put: <K extends Kind>(kind: K, item: ItemOf<K>) => void;
  remove: (kind: Kind, ids: Id[]) => void;
  /** Merge imported items (from a JSON export) without touching anything else. */
  importItems: (data: Partial<Pick<LibraryState, Kind>>) => number;
  confirmDiet: (id: Id, diet: Diet, fits: boolean) => void;
};

const KINDS: Kind[] = ['recipes', 'cookbooks', 'mealplans', 'units', 'ingredients', 'utensils'];

const EMPTY: LibraryState = {
  recipes: {}, cookbooks: {}, mealplans: {}, units: {}, ingredients: {}, utensils: {}, hiddenBuiltIns: [], dietConfirmations: {}, updatedAt: 0,
};

export const useLibrary = create<LibraryState & Actions>()(
  persist(
    set => ({
      ...EMPTY,
      put: (kind, item) =>
        set(s => ({ [kind]: { ...s[kind], [item.id]: { ...item, ...('updatedAt' in item ? { updatedAt: Date.now() } : {}) } }, updatedAt: Date.now() }) as Partial<LibraryState>),
      remove: (kind, ids) =>
        set(s => {
          const next = { ...s[kind] } as Record<Id, unknown>;
          for (const id of ids) delete next[id];
          const builtIn = ids.filter(id => (kind === 'ingredients' && CATALOG_INGREDIENTS[id]) || (kind === 'utensils' && CATALOG_UTENSILS[id]));
          return { [kind]: next, hiddenBuiltIns: [...new Set([...s.hiddenBuiltIns, ...builtIn])], updatedAt: Date.now() } as Partial<LibraryState>;
        }),
      confirmDiet: (id, diet, fits) =>
        set(s => {
          const now = (s.dietConfirmations[id] ?? []).filter(d => d !== diet);
          return { dietConfirmations: { ...s.dietConfirmations, [id]: fits ? [...now, diet] : now }, updatedAt: Date.now() };
        }),
      importItems: data => {
        let n = 0;
        set(s => {
          const patch: Partial<LibraryState> = {};
          // Only known kinds: older backups may still carry techniques, which no longer exist.
          for (const kind of (Object.keys(data) as Kind[]).filter(k => KINDS.includes(k))) {
            const incoming = data[kind] ?? {};
            n += Object.keys(incoming).length;
            (patch as Record<Kind, unknown>)[kind] = { ...s[kind], ...incoming };
          }
          return { ...patch, updatedAt: Date.now() };
        });
        return n;
      },
    }),
    {
      name: 'library',
      storage: persistStorage,
      version: 2,
      migrate: (state, version) => migrateLibrary(state, version) as LibraryState & Actions,
    },
  ),
);

/** v2: techniques were removed (people learn by cooking); drop them and the recipes' links to them. */
export function migrateLibrary(state: unknown, version: number): LibraryState {
  const s = { ...(state as LibraryState & { techniques?: unknown }) };
  if (version < 2) {
    delete s.techniques;
    s.recipes = Object.fromEntries(Object.entries(s.recipes ?? {}).map(([id, r]) => {
      const { techniqueIds: _t, ...rest } = r as Recipe & { techniqueIds?: unknown };
      return [id, rest];
    }));
  }
  return s;
}

export const newId = () => nanoid(12);

// ---- Selectors: merge built-in content with the user's library -------------------------

export function useAllIngredients(): Record<Id, Ingredient> {
  const own = useLibrary(s => s.ingredients);
  const hidden = useLibrary(s => s.hiddenBuiltIns);
  return useMemo(() => {
    const out: Record<Id, Ingredient> = {};
    for (const [id, i] of Object.entries(CATALOG_INGREDIENTS)) if (!hidden.includes(id)) out[id] = i;
    return { ...out, ...own };
  }, [own, hidden]);
}

export function useAllUtensils(): Record<Id, Utensil> {
  const own = useLibrary(s => s.utensils);
  const hidden = useLibrary(s => s.hiddenBuiltIns);
  return useMemo(() => {
    const out: Record<Id, Utensil> = {};
    for (const [id, u] of Object.entries(CATALOG_UTENSILS)) if (!hidden.includes(id)) out[id] = u;
    return { ...out, ...own };
  }, [own, hidden]);
}

/** A recipe from the library or from a country cookbook (read-only). */
export function useRecipe(id: Id | undefined): { recipe: Recipe | undefined; readOnly: boolean } {
  const own = useLibrary(s => (id ? s.recipes[id] : undefined));
  if (own) return { recipe: own, readOnly: false };
  return { recipe: id ? CONTENT_RECIPES[id] : undefined, readOnly: true };
}

/** Recipe lookup across library + content, for lists (planner, grocery, search). */
export function useRecipeLookup(): (id: Id) => Recipe | undefined {
  const own = useLibrary(s => s.recipes);
  return useMemo(() => (id: Id) => own[id] ?? CONTENT_RECIPES[id], [own]);
}

export function blankRecipe(title: string): Recipe {
  return {
    id: newId(), title, explainer: '', servings: 2, durationMin: 0, priceCents: 0,
    ingredients: [], utensils: [], steps: [], updatedAt: Date.now(),
  };
}

export type CreatableKind = 'recipes' | 'cookbooks' | 'mealplans' | 'ingredients' | 'utensils' | 'units';

/** Creates and stores a blank item; returns its id. */
export function createBlank(kind: CreatableKind, title: string): Id {
  const id = newId();
  const now = Date.now();
  const put = useLibrary.getState().put;
  switch (kind) {
    case 'recipes':
      put('recipes', { ...blankRecipe(title), id });
      break;
    case 'cookbooks':
      put('cookbooks', { id, title, explainer: '', sections: [{ id: newId(), title: '', note: '', recipeIds: [] }], updatedAt: now });
      break;
    case 'mealplans':
      put('mealplans', { id, title, explainer: '', startWeekday: 'monday', cycleDays: 7, entries: [], updatedAt: now });
      break;
    case 'ingredients':
      put('ingredients', { id, title, aisle: 'other', notDiet: [], synonyms: [], explainer: '', builtIn: false });
      break;
    case 'utensils':
      put('utensils', { id, title, aisle: 'other', synonyms: [], explainer: '', builtIn: false });
      break;
    case 'units':
      put('units', { id, title, symbol: '', searchTerms: [], dimension: null, toBase: null });
      break;
  }
  return id;
}
